import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Coins, Plus } from "lucide-react";
import { expandSearch } from "@/lib/api-client";
import { toast } from "sonner";
import { parseApiError } from "@/lib/api-error";
import { useCredits } from "@/contexts/CreditsContext";
import { cn } from "@/lib/utils";

const EXPAND_OPTIONS = [10, 20, 50] as const;

interface ExpandSearchDialogProps {
  jobId: string;
  disabled?: boolean;
  onExpansionStarted: () => void;
}

export function ExpandSearchDialog({
  jobId,
  disabled,
  onExpansionStarted,
}: ExpandSearchDialogProps) {
  const { t } = useTranslation("results");
  const { costOf, balance, refreshCredits } = useCredits();
  const cost = costOf("search_expand");
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleExpand(count: number) {
    setSubmitting(true);
    try {
      await expandSearch(jobId, count);
      // CST209: charge landed, refresh balance immediately. Fire-and-forget —
      // mirrors SearchWizard.tsx:178 pattern. Do not await: refreshCredits
      // failure is non-fatal and should not block dialog close.
      refreshCredits();
      setOpen(false);
      onExpansionStarted();
    } catch (err: unknown) {
      const { message } = parseApiError(err);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" disabled={disabled}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          {t("expandDialog.triggerButton")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("expandDialog.title")}</DialogTitle>
          <DialogDescription>{t("expandDialog.description")}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 justify-center py-4">
          {EXPAND_OPTIONS.map((count) => (
            <Button
              key={count}
              variant="outline"
              size="lg"
              disabled={submitting}
              onClick={() => handleExpand(count)}
              className="min-w-[80px]"
              aria-label={t("expandDialog.optionAriaLabel", { count, cost })}
            >
              {cost > 0 && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1",
                    balance < cost && "text-destructive",
                  )}
                >
                  <Coins className="h-3.5 w-3.5" /> {cost}
                </span>
              )}
              <span>+{count}</span>
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
